/**
 * Points Page - Fortunio Points balance and transaction history
 * 
 * Displays user's FP balance, achievements, transaction history with grouping options,
 * and guest conversion CTA.
 */

import { logger } from '../lib/logger.js';
import { ensureAuthModalExists, authModal } from '../components/auth-modal.js';
import { balanceService } from '../services/balance-service.js';

interface PointsBalanceResponse {
    success: boolean;
    balance: number;
    currency: string;
    last_earned_at: string | null;
}

interface Transaction {
    id: number;
    amount: number;
    balance_after: number;
    rule_key: string;
    display_reason: string | null;
    reference_id: string;
    created_at: string;
}

interface TransactionHistoryResponse {
    success: boolean;
    transactions?: Transaction[];
    summaries?: HistorySummary[];
    total: number;
    page: number;
    per_page: number;
    pages?: number;
    group_by?: string;
    error?: string;
}

interface HistorySummary {
    period: string;
    total_amount: number;
    transaction_count: number;
}

interface UserInfo {
    success: boolean;
    authenticated: boolean;
    is_guest?: boolean;
    user?: {
        id: string;
        username: string;
        display_name: string | null;
        email: string;
        is_guest: boolean;
    };
}

// Achievement interfaces (Step 21)
interface Achievement {
    id: string;
    name: string;
    description: string;
    category: string;
    icon: string;
    points: number;
    is_unlocked: boolean;
    unlocked_at: string | null;
    // Catalogue-sourced fields (V4 — from include_catalogue_info=True)
    game_slug: string | null;
    family_slug: string | null;
    game_name: string | null;
    game_icon: string | null;
    family_name: string | null;
    family_icon: string | null;
}

interface GameHighlight {
    game_slug: string;
    game_name: string;
    game_icon: string;
    label: string;
    unit: string;
    value: number;
    games_played: number;
}

interface HighlightsResponse {
    success: boolean;
    highlights: GameHighlight[];
}

interface AchievementsResponse {
    success: boolean;
    achievements: Achievement[];
    total_unlocked: number;
    total_achievements: number;
    total_points_from_achievements: number;
}

let currentPage = 1;
let currentGroupBy: 'transaction' | 'day' | 'month' = 'transaction';
let isAuthenticated = false;

document.addEventListener('DOMContentLoaded', async () => {
    // Check if user is authenticated
    await checkAuthAndLoadData();
    
    // Setup group-by buttons
    document.querySelectorAll('[data-group]').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            document.querySelectorAll('[data-group]').forEach(b => b.classList.remove('active'));
            target.classList.add('active');
            currentGroupBy = target.dataset.group as 'transaction' | 'day' | 'month';
            currentPage = 1;
            loadHistory();
        });
    });
    
    // Load more button
    document.getElementById('load-more-btn')?.addEventListener('click', () => {
        currentPage++;
        loadHistory(true);
    });
});

async function checkAuthAndLoadData(): Promise<void> {
    try {
        // First check if user is a guest via /me endpoint
        const meResponse = await fetch('/me');
        const meData: UserInfo = await meResponse.json();
        
        // Show guest view if not authenticated OR is_guest flag is set
        if (!meData.authenticated || meData.is_guest || meData.user?.is_guest) {
            showGuestView();
            return;
        }
        
        // Authenticated non-guest user - fetch balance
        const response = await fetch('/api/user/points/balance');
        
        if (response.status === 401) {
            // Fallback for unauthenticated
            showGuestView();
            return;
        }
        
        if (!response.ok) {
            logger.error('Failed to fetch balance:', response.status, response.statusText);
            const taglineEl = document.getElementById('balance-tagline');
            if (taglineEl) {
                taglineEl.textContent = 'Couldn\u2019t load your balance right now';
            }
            return;
        }
        
        isAuthenticated = true;
        const data: PointsBalanceResponse = await response.json();
        displayBalance(data);
        
        // Subscribe to balance changes for real-time updates
        balanceService.subscribe((newBalance) => {
            updateBalanceDisplay(newBalance);
            // Reload history to show new transactions
            currentPage = 1;
            loadHistory();
        });
        
        // Sync balanceService cache with fetched data
        balanceService.updateBalance(data.balance);
        
        // Load achievements, highlights, and history in parallel
        await Promise.all([
            loadAchievements(),
            loadHighlights(),
            loadHistory()
        ]);
    } catch (err) {
        logger.error('Failed to load balance:', err);
        const taglineEl = document.getElementById('balance-tagline');
        if (taglineEl) {
            taglineEl.textContent = 'Unable to load balance';
        }
    }
}

function showGuestView(): void {
    const balanceEl = document.getElementById('fp-balance');
    const buttonEl = document.getElementById('sign-in-btn') as HTMLButtonElement;
    const textEl = document.getElementById('balance-tagline');
    const historyCard = document.getElementById('history-card');
    const achievementsCard = document.getElementById('achievements-card');
    const recordsCard = document.getElementById('records-card');
    const groupByContainer = document.querySelector('.btn-group');
    const guestMessage = document.getElementById('guest-message');
    
    if (balanceEl) balanceEl.textContent = '0';
    if (buttonEl) {
        buttonEl.classList.remove('d-none');
        buttonEl.addEventListener('click', () => {
            ensureAuthModalExists();
            authModal.show();
        });
    }
    if (textEl) textEl.classList.add('d-none');
    if (historyCard) historyCard.style.display = 'none';
    if (achievementsCard) achievementsCard.style.display = 'none';
    if (recordsCard) recordsCard.style.display = 'none';
    if (groupByContainer) (groupByContainer as HTMLElement).style.display = 'none';
    if (guestMessage) guestMessage.classList.remove('d-none');
}

function displayBalance(data: PointsBalanceResponse): void {
    const balanceEl = document.getElementById('fp-balance');
    const taglineEl = document.getElementById('balance-tagline');
    const buttonEl = document.getElementById('sign-in-btn') as HTMLButtonElement;

    if (!balanceEl || !taglineEl) return;
    
    balanceEl.textContent = data.balance.toLocaleString();
    
    // Hide button, show text for authenticated users
    if (buttonEl) buttonEl.classList.add('d-none');
    
    // Generate tagline based on balance
    let tagline = '';
    if (data.balance === 0) {
        tagline = 'Play games to start earning!';
    } else if (data.balance < 100) {
        tagline = 'Nice start! Keep playing to earn more.';
    } else if (data.balance < 500) {
        tagline = 'You\'re on a roll! Great progress.';
    } else {
        tagline = 'Impressive collection! You\'re a Fortunio master!';
    }
    
    taglineEl.textContent = tagline;
}

/**
 * Update just the balance number (used by balanceService subscription)
 */
function updateBalanceDisplay(balance: number): void {
    const balanceEl = document.getElementById('fp-balance');
    const taglineEl = document.getElementById('balance-tagline');
    
    if (!balanceEl || !taglineEl) return;
    
    balanceEl.textContent = balance.toLocaleString();
    
    // Generate tagline based on balance
    let tagline = '';
    if (balance === 0) {
        tagline = 'Play games to start earning!';
    } else if (balance < 100) {
        tagline = 'Nice start! Keep playing to earn more.';
    } else if (balance < 500) {
        tagline = 'You\'re on a roll! Great progress.';
    } else {
        tagline = 'Impressive collection! You\'re a Fortunio master!';
    }
    
    taglineEl.textContent = tagline;
    
    // Brief highlight animation (reuses daily-reward animation)
    balanceEl.classList.add('balance-increase');
    setTimeout(() => balanceEl.classList.remove('balance-increase'), 600);
}

async function loadHistory(append = false): Promise<void> {
    const container = document.getElementById('transactions-container');
    const loadMoreContainer = document.getElementById('load-more-container');
    
    if (!container) return;
    
    if (!append) {
        container.innerHTML = '<p class="text-muted text-center py-3">Loading...</p>';
    }
    
    try {
        const response = await fetch(`/api/user/points/history?page=${currentPage}&group_by=${currentGroupBy}`);
        
        if (!response.ok) {
            logger.error('Failed to fetch history:', response.status, response.statusText);
            container.innerHTML = '<p class="text-danger text-center py-3">Couldn\u2019t load your history — try refreshing the page.</p>';
            return;
        }
        
        const data: TransactionHistoryResponse = await response.json();
        
        if (!data.success) {
            logger.error('History request unsuccessful:', data.error);
            container.innerHTML = '<p class="text-danger text-center py-3">Couldn\u2019t load your history — try refreshing the page.</p>';
            return;
        }
        
        if (currentGroupBy === 'transaction' && data.transactions) {
            renderTransactions(data.transactions, append);
            if (loadMoreContainer) {
                loadMoreContainer.style.display = (data.pages && currentPage < data.pages) ? 'block' : 'none';
            }
        } else if (data.summaries) {
            renderSummaries(data.summaries, append);
            if (loadMoreContainer) {
                loadMoreContainer.style.display = data.summaries.length >= 20 ? 'block' : 'none';
            }
        }
    } catch (err) {
        logger.error('Failed to load history:', err);
        container.innerHTML = '<p class="text-danger text-center py-3">Failed to load history</p>';
    }
}

function renderTransactions(transactions: Transaction[], append: boolean): void {
    const container = document.getElementById('transactions-container');
    if (!container) return;
    
    if (transactions.length === 0 && !append) {
        container.innerHTML = '<p class="text-muted text-center py-3">Your points story starts here — play a game to earn your first FP!</p>';
        return;
    }
    
    let html = append ? container.innerHTML : '';
    
    transactions.forEach(tx => {
        const amountClass = tx.amount >= 0 ? 'text-success' : 'text-danger';
        const amountPrefix = tx.amount >= 0 ? '+' : '';
        const reason = tx.display_reason || formatRuleKey(tx.rule_key);
        const date = new Date(tx.created_at).toLocaleDateString();
        
        html += `
            <div class="d-flex justify-content-between align-items-center border-bottom py-2">
                <div>
                    <strong>${reason}</strong>
                    <br><small class="text-muted">${date}</small>
                </div>
                <span class="${amountClass} fw-bold">${amountPrefix}${tx.amount} FP</span>
            </div>
        `;
    });
    
    container.innerHTML = html;
}

function renderSummaries(summaries: HistorySummary[], append: boolean): void {
    const container = document.getElementById('transactions-container');
    if (!container) return;
    
    if (summaries.length === 0 && !append) {
        container.innerHTML = '<p class="text-muted text-center py-3">Nothing here yet — your earnings will show up as you play.</p>';
        return;
    }
    
    let html = append ? container.innerHTML : '';
    
    summaries.forEach(s => {
        const amountClass = s.total_amount >= 0 ? 'text-success' : 'text-danger';
        const amountPrefix = s.total_amount >= 0 ? '+' : '';
        
        html += `
            <div class="d-flex justify-content-between align-items-center border-bottom py-2">
                <div>
                    <strong>${s.period}</strong>
                    <br><small class="text-muted">${s.transaction_count} transaction${s.transaction_count !== 1 ? 's' : ''}</small>
                </div>
                <span class="${amountClass} fw-bold">${amountPrefix}${s.total_amount} FP</span>
            </div>
        `;
    });
    
    container.innerHTML = html;
}

function formatRuleKey(ruleKey: string): string {
    if (!ruleKey) return 'Unknown';
    
    // Convert rule key to readable format
    // e.g., 'yatzy.game_complete.solo.v1' -> 'Game Complete (Solo)'
    const parts = ruleKey.split('.');
    const event = parts[1]?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Reward';
    const mode = parts[2] ? ` (${parts[2]})` : '';
    return event + mode;
}

// Achievement functions (Step 21)

async function loadHighlights(): Promise<void> {
    const card = document.getElementById('records-card');
    const container = document.getElementById('records-container');
    if (!container) return;

    try {
        const response = await fetch('/api/user/stats/highlights');

        if (!response.ok) {
            logger.error('Failed to fetch highlights:', response.status, response.statusText);
            return; // Show nothing — records card stays hidden
        }

        const data: HighlightsResponse = await response.json();

        if (!data.success || data.highlights.length === 0) {
            // No records yet — keep card hidden
            return;
        }

        renderHighlights(data.highlights);
        if (card) card.classList.remove('d-none');
    } catch (err) {
        logger.error('Failed to load highlights:', err);
        // Silently suppress — records card is a bonus, not critical
    }
}

function renderHighlights(highlights: GameHighlight[]): void {
    const container = document.getElementById('records-container');
    if (!container) return;

    let html = '<div class="row g-3">';
    highlights.forEach(h => {
        const valueStr = h.unit ? `${h.value.toLocaleString()} ${h.unit}` : h.value.toLocaleString();
        const gamesStr = `${h.games_played.toLocaleString()} game${h.games_played !== 1 ? 's' : ''} played`;
        html += `
            <div class="col-6 col-md-4">
                <div class="text-center p-2 rounded border">
                    <i class="bi ${h.game_icon} fs-4 text-primary mb-1 d-block"></i>
                    <div class="fw-bold small">${h.game_name}</div>
                    <div class="text-muted" style="font-size:0.7rem;">${h.label}</div>
                    <div class="fs-5 fw-bold text-primary">${valueStr}</div>
                    <div class="text-muted" style="font-size:0.7rem;">${gamesStr}</div>
                </div>
            </div>
        `;
    });
    html += '</div>';

    container.innerHTML = html;
}

async function loadAchievements(): Promise<void> {
    const container = document.getElementById('achievements-container');
    const progressEl = document.getElementById('achievements-progress');
    
    if (!container) return;
    
    try {
        const response = await fetch('/api/achievements/me');
        
        if (!response.ok) {
            logger.error('Failed to fetch achievements:', response.status, response.statusText);
            container.innerHTML = '<p class="text-danger text-center py-3">Couldn\u2019t load achievements right now — try again in a moment.</p>';
            return;
        }
        
        const data: AchievementsResponse = await response.json();
        
        if (!data.success) {
            logger.error('Achievements request unsuccessful');
            container.innerHTML = '<p class="text-danger text-center py-3">Couldn\u2019t load achievements right now — try again in a moment.</p>';
            return;
        }
        
        // Update progress badge
        if (progressEl) {
            progressEl.textContent = `${data.total_unlocked} / ${data.total_achievements}`;
        }
        
        renderAchievements(data.achievements);
    } catch (err) {
        logger.error('Failed to load achievements:', err);
        container.innerHTML = '<p class="text-danger text-center py-3">Couldn\u2019t load achievements right now — try again in a moment.</p>';
    }
}

function renderAchievements(achievements: Achievement[]): void {
    const container = document.getElementById('achievements-container');
    if (!container) return;

    if (achievements.length === 0) {
        container.innerHTML = '<p class="text-muted text-center py-3">Achievements are on their way — keep playing to unlock them!</p>';
        return;
    }

    // --- Build ordered group keys ---
    // Group key: game_slug if present, else family_slug, else category
    // Order: game-specific groups first (by game_name), then cross-game groups (by category)
    interface GroupMeta { label: string; icon: string | null; }
    const groups = new Map<string, { meta: GroupMeta; items: Achievement[] }>();

    achievements.forEach(ach => {
        let groupKey: string;
        let meta: GroupMeta;

        if (ach.game_slug && ach.game_name) {
            groupKey = `game:${ach.game_slug}`;
            meta = { label: ach.game_name, icon: ach.game_icon };
        } else if (ach.family_slug && ach.family_name) {
            groupKey = `family:${ach.family_slug}`;
            meta = { label: `${ach.family_name} Family`, icon: ach.family_icon };
        } else {
            // Cross-game — use category display name
            const categoryLabels: Record<string, string> = {
                games: 'All Games',
                scores: 'High Scores',
                skills: 'Skills',
                streaks: 'Streaks',
                social: 'Social',
            };
            groupKey = `cat:${ach.category}`;
            meta = { label: categoryLabels[ach.category] ?? ach.category, icon: null };
        }

        if (!groups.has(groupKey)) {
            groups.set(groupKey, { meta, items: [] });
        }
        groups.get(groupKey)!.items.push(ach);
    });

    let html = '';

    groups.forEach(({ meta, items }) => {
        const iconHtml = meta.icon
            ? `<i class="bi ${meta.icon} me-1"></i>`
            : '';
        html += `<h6 class="text-muted mt-3 mb-2">${iconHtml}${meta.label}</h6>`;
        html += '<div class="row g-2">';

        items.forEach(ach => {
            const isUnlocked = ach.is_unlocked;
            const cardClass = isUnlocked ? 'border-success' : 'border-secondary opacity-50';
            const iconOpacity = isUnlocked ? '' : 'opacity-50';
            const checkmark = isUnlocked
                ? '<i class="bi bi-check-circle-fill text-success position-absolute top-0 end-0 m-1"></i>'
                : '';
            const unlockedDate = ach.unlocked_at
                ? `<small class="text-muted d-block">Unlocked ${new Date(ach.unlocked_at).toLocaleDateString()}</small>`
                : '';

            html += `
                <div class="col-6 col-md-4">
                    <div class="card h-100 ${cardClass} position-relative">
                        ${checkmark}
                        <div class="card-body text-center p-2">
                            <div class="fs-2 ${iconOpacity}">${ach.icon}</div>
                            <h6 class="card-title mb-1" style="font-size: 0.85rem;">${ach.name}</h6>
                            <p class="card-text text-muted mb-1" style="font-size: 0.75rem;">${ach.description}</p>
                            <span class="badge ${isUnlocked ? 'bg-success' : 'bg-secondary'}">${ach.points} FP</span>
                            ${unlockedDate}
                        </div>
                    </div>
                </div>
            `;
        });

        html += '</div>';
    });

    container.innerHTML = html;
}
